"use client"

import { Text } from "@/elements"
import { FragmentType, useFragment } from "@/gql"
import { InformationCircleIcon } from "@heroicons/react/24/outline"
import { formatDistanceStrict } from "date-fns"
import { Action, actionProgressPercentage, determineJobPostingAction } from "../../utilts"
import * as Tooltip from "@radix-ui/react-tooltip"
import { MyJobsJobPostMetadataFragmentDoc } from "@/gql/graphql"
import { JobTitleColorised } from "@/components/JobTitleColorised"
import { CoverImageWithEdit } from "../CoverImageWithEdit"
import adjustDateForTimezone from "@/utils/dates"

interface Props {
  jobPosting: FragmentType<typeof MyJobsJobPostMetadataFragmentDoc> | null | undefined
  showStatus?: boolean
  bottomRightElement?: () => React.ReactNode
  eagerImage: boolean
}

export const MyJobsJobPostMetadataMobile: React.FC<Props> = ({
  showStatus = true,
  bottomRightElement,
  eagerImage,
  ...props
}) => {
  const jobPosting = useFragment(MyJobsJobPostMetadataFragmentDoc, props.jobPosting)

  const jobStatus = determineJobPostingAction(jobPosting)

  return (
    <div className="flex flex-col gap-y-6 md:hidden">
      <div className="flex h-[72px] flex-row items-center gap-x-5">
        <CoverImageWithEdit jobPosting={jobPosting} />

        <div className="flex h-full flex-col justify-center gap-y-3 pb-[1px] pt-[2px] md:hidden">
          <div className="flex flex-col gap-y-2">
            <JobTitleColorised jobPost={jobPosting} textVariant="h6" commonWordColor="text-fg-2" maxLines={2} />

            <div className="flex flex-row flex-wrap items-center gap-x-2">
              <Text variant="label" className="text-fg-2">
                {formatDistanceStrict(
                  jobPosting?.createdAt
                    ? adjustDateForTimezone(jobPosting.createdAt)!
                    : adjustDateForTimezone(new Date().toISOString())!,
                  adjustDateForTimezone(new Date().toISOString())!,
                  {
                    addSuffix: true,
                  }
                )}
              </Text>
              <div className="size-1 rounded-full bg-fg-2" />
              <Text variant="label" element="span" className="text-fg-2">
                {jobPosting?.views} views
              </Text>
              <div className="size-1 rounded-full bg-fg-2" />

              <Text variant="label" className="text-fg-2">
                {jobPosting?.applicationCount} applications
              </Text>
            </div>
          </div>
        </div>
      </div>
      <div className="flex w-full flex-row items-center justify-between">
        {showStatus ? (
          <div className="flex flex-col gap-y-3">
            <div className="flex flex-row items-center gap-x-[6px]">
              <Text variant="label" className="text-fg-2">
                Next
              </Text>
              <Text variant="label" className="text-fg-1">
                {jobStatus}
              </Text>
            </div>
            <div className="h-1 w-[200px] rounded-full bg-bg-3 md:max-w-[274px]">
              <div
                style={{ width: actionProgressPercentage(jobStatus) }}
                className="h-full rounded-full bg-primary-3"></div>
            </div>
          </div>
        ) : (
          <div />
        )}
        <div>{bottomRightElement?.()}</div>
      </div>
    </div>
  )
}

const statusToTooltip: Record<Action, { content: string; width: string }> = {
  [Action.SEND_INVITE]: { content: "Invite creatives to apply to your job", width: "253px" },
  [Action.REVIEW_APPLICATIONS]: {
    content: `Navigate to your unreviewed applications and mark them as “interested” or “passed”`,
    width: "269px",
  },
  [Action.MESSAGE_APPLICANTS]: {
    content: "Chat with prospective creatives to confirm interest and availability",
    width: "253px",
  },
  //[Action.HIRE]: { content: "Report who you hired for your internal records", width: "191px" },
  [Action.CLOSE_JOB]: {
    content: `Close your job to remove it 
  from the active section`,
    width: "205px",
  },
  [Action.CLOSED]: {
    content: `This job is closed`,
    width: "205px",
  },
}
